<template>
<div
    role="button"
    tabindex="0"
    class="spotlight-container"
    :style="{ backgroundImage: `url(${slide.spotlightWide?.data?.attributes?.url}?format=auto&width=1200)` }"
    v-on:click="onSlideClick(slide)"
    v-on:keydown.enter="onSlideClick(slide)"
>
    <div class="gradient-left" />
    <div class="overlay">
        <NuxtImg
            v-if="wordmarkUrl"
            :src="`${wordmarkUrl}?format=auto&width=450`"
            :alt="product?.wordmark?.data?.attributes?.alternativeText || ''"
            class="logo"
            :class="slide.wordmarkSize"
        />
        <span v-if="slide.title" class="title">
            {{ slide.title }}
        </span>
        <div v-if="slide.statusLabel" class="platform-status">
            <span
                class="status-label"
            >
                {{ slide.statusLabel === 'OUT NOW' ? slide.statusLabel + ':' : slide.statusLabel }}
            </span>
            <div v-if="!product.displayWishlistUI" v-on:click.stop>
                <StorefrontIcons
                    v-if="platforms" :pack="product"
                    :platform-options="platforms"
                />
            </div>
        </div>
        <span
            class="description x-b-17"
            :style="{
                WebkitLineClamp: slide.type === 'general' ? 8 : 3
            }"
        >
            {{ slide.description }}
        </span>
        <div v-if="slide.cta" class="ctas">
            <template v-if="slide.cta.type === 'wishlist'">
                <div v-on:click.stop>
                    <WishlistButton
                        :slug="product.slug"
                        :wishlist-url="slide.cta.url"
                        :cta-color="slide.cta.color || defaultCtaColor"
                        :cta-hover-color="slide.cta.hoverColor || defaultCtaHoverColor"
                        :cta-dark="slide.cta.dark"
                    />
                </div>
                <NuxtLink :to="getSlideRoute(slide)" class="cta-button about-button" v-on:click.stop>
                    <span class="x-l-16 about-label">About the Game</span>
                </NuxtLink>
            </template>
            <template v-else>
                <NuxtLink
                    v-if="!isExternalURL(slide.cta.url)"
                    :to="slide.cta?.url || getSlideRoute(slide)"
                    class="cta-button learn-button"
                    :style="{
                        '--cta-button-bg': slide.cta?.color || 'var(--default-cta-button-bg)',
                        '--cta-button-hover-bg': slide.cta?.hoverColor || 'var(--default-cta-button-bg-hover)',
                        '--cta-button-color': slide.cta?.dark ? 'var(--neutral-50)' : 'var(--neutral-900)'
                    }"
                >
                    <span class="x-l-17 learn-label">{{ slide.cta.label }}</span>
                    <UiIcon icon="arrow-right" alt="arrow right" :classes="['arrow-right']" />
                </NuxtLink>
                <a
                    v-else
                    :href="slide.cta.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="cta-button learn-button"
                    :style="{
                        '--cta-button-bg': slide.cta?.color || 'var(--default-cta-button-bg)',
                        '--cta-button-hover-bg': slide.cta?.hoverColor || 'var(--default-cta-button-bg-hover)',
                        '--cta-button-color': slide.cta?.dark ? 'var(--neutral-50)' : 'var(--neutral-900)'
                    }"
                >
                    <span class="x-l-17 learn-label">{{ slide.cta.label }}</span>
                    <UiIcon icon="arrow-right" alt="arrow right" :classes="['arrow-right']" />
                </a>
            </template>
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { JBGWeb } from '$types/JBGWeb'
import UiIcon from '~/public/images/icons/ui/UiIcon.vue'
import WishlistButton from '../shopping/WishlistButton.vue'
import StorefrontIcons from '../shopping/StorefrontIcons.vue'

const props = defineProps({
    slide: {
        type: Object as PropType<JBGWeb.HomepageFeature>,
        required: true
    },
    platforms: {
        type: Array as PropType<JBGWeb.PlatformOption[]>
    }
})

const localeRoute = useLocaleRoute()

const product = computed(() => {
    switch (props.slide.type) {
    case 'pack':
        return props.slide.packDetail.data.attributes
    case 'game':
        return props.slide.gameDetail.data.attributes
    case 'external-game':
        return props.slide.externalGame.data.attributes
    case 'bundle':
        return props.slide.bundleDetail.data.attributes
    default:
        return null
    }
})

const wordmarkUrl = computed(() => product.value?.wordmark?.data?.attributes?.url || null)

const defaultCtaColor = 'linear-gradient(180deg, #DFE5EE 0%, #E1EAF2 50%, #DFE5EE 100%)'
const defaultCtaHoverColor = 'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.20) 100%), linear-gradient(180deg, #DFE5EE 0%, #E1EAF2 50%, #E3E9F2 100%)'

const isExternalURL = (url: string) => {
    if (!url) return false
    try {
        return new URL(url).origin !== window.location.origin
    } catch {
        return false
    }
}

function getSlideRoute(slide: JBGWeb.HomepageFeature) {
    if (slide.type === 'pack') {
        return localeRoute({
            name: 'games-packs-packslug',
            params: { packslug: slide.slug }
        })
    }

    if (slide.type === 'external-game') {
        return localeRoute({
            name: 'games-publishing-externalgameslug',
            params: { externalgameslug: slide.slug }
        })
    }

    return localeRoute({
        name: 'games-gameslug',
        params: { gameslug: slide.slug }
    })
}

function generalSlideRoute(slide: JBGWeb.HomepageFeature) {
    if (isExternalURL(slide.cta.url)) {
        window.open(slide.cta.url, '_blank', 'noopener noreferrer')
    } else {
        // internal path so strip https://jackboxgames.com and use rest of path
        const internalPath = slide.cta.url.replace(window.location.origin, '')
        void navigateTo(internalPath)
    }
}

function onSlideClick(slide: JBGWeb.HomepageFeature) {
    if (slide.type === 'general') {
        generalSlideRoute(slide)
    } else {
        void navigateTo(getSlideRoute(slide))
    }
}

</script>

<style lang="scss" scoped>
@import "../../styles/kit.scss";

.spotlight-container {
    display: flex;
    position: relative;
    border-radius: 16px;
    aspect-ratio: 1.9 / 1;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;

    .gradient-left {
        position: absolute;
        left: 0px;
        bottom: 0px;
        width: 50%;
        height: 100%;
        background: linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.70) -2.95%,
            rgba(0, 0, 0, 0.48) 58.82%,
            rgba(0, 0, 0, 0.00) 100%);
    }

    .overlay {
        display: flex;
        flex-direction: column;
        position: absolute;
        left: 0px;
        bottom: 0px;
        width: 40%;
        padding: 0 0 20px 20px;

        @include mq-small {
            padding: 0 0 12px 16px;
        }

        .logo {
            margin-bottom: 16px;
            object-fit: contain;
            max-height: 150px;

            &.large {
                width: 100%;
            }

            &.medium {
                width: 270px;

                @include mq-medium {
                    width: 240px;
                }

                @include mq-small {
                    width: 190px;
                }
            }

            &.small {
                width: 240px;

                @include mq-small {
                    width: 190px;
                }
            }
        }

        .title {
            color: #FFF;
            font-size: 28px;
            line-height: 28px;
            font-weight: 680;
            margin-bottom: 8px;
        }

        .platform-status {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 9px;
            margin-bottom: 8px;
            min-width: 0;

            .status-label {
                color: #FFF;
                font-size: 15px;
                line-height: 16px;
                font-weight: 650;

                @include mq-medium {
                    font-size: 14px;
                    line-height: 14px;
                }

                @include mq-small {
                    font-size: 13px;
                    line-height: 13px;
                }
            }
        }

        .description {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            color: #F5F5F5;
            margin-bottom: 16px;

            @include mq-medium {
                font-size: 16px;
            }

            @include mq-small {
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
                font-size: 13px;
                font-weight: 480;
                line-height: 136%;
                letter-spacing: 0.2px;
            }
        }

        .cta-button {
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            gap: 9px;
            cursor: pointer;
            width: 100%;
            height: 44px;

            @include mq-small {
                height: 40px;
                margin-bottom: 12px;
            }
        }

        .learn-button {
            --default-cta-button-bg: linear-gradient(180deg, #DFE5EE 0%, #E1EAF2 50%, #DFE5EE 100%);
            --default-cta-button-bg-hover: linear-gradient(180deg, #EDF1F7 0%, #F0F6FC 50%, #EDF1F7 100%);

            background: var(--cta-button-bg);

            .learn-label {
                color: var(--cta-button-color);

                @include mq-small {
                    font-size: 16px;
                }
            }

            &:hover {
                background: var(--cta-button-hover-bg);
                box-shadow: 0 0 8px 1px rgba(255, 255, 255, 0.05) inset;
            }

            :deep(.arrow-right) {
                height: 22px;
                width: 22px;
                color: var(--cta-button-color);
            }
        }

        .about-button {
            text-align: center;
            margin-top: 16px;
            height: 16px;
            background: var(--cta-bg);

            &:hover {
                transform: none;
                opacity: 0.8;
            }

            @include mq-small {
                height: 15px;
            }

            .about-label {
                @include mq-small {
                    font-size: 15px;
                }
            }
        }
    }
}

:deep(.plat-icons-container) {
    padding: 0px;
    gap: 9px;
    flex-wrap: nowrap;
    min-width: 0;
}

:deep(.storefront-icon) {
    color: white;
    width: clamp(16px, 2vw, 22px) !important;
    height: clamp(16px, 2vw, 22px) !important;
    flex-shrink: 1;
    margin: 0px !important;

    &:hover {
        color: var(--primary-200)
    }
}

</style>
